import { useAuth } from '@/contexts/auth-context';
import { useUpdateBillingTaxProfile } from '@/hooks/actions/use-update-billing-tax-profile';
import { useBillingActionScope } from '@/hooks/actions/use-billing-action-scope';
import {
  readBillingTaxProfile,
  useBillingTaxProfileQuery,
} from '@/hooks/queries/use-billing-tax-profile-query';
import { readBillingContext } from '@/libs/billing-access';
import { Button, Heading, Stack, Text } from '@chakra-ui/react';
import type {
  CompanyDetails,
  OrganizationBillingContext,
  OrganizationBillingTaxProfileData,
  OrganizationBillingTaxProfileResponseData,
} from '@fluenceur/types';
import { isTaxResidenceCountry } from '@fluenceur/types';
import { NoticePanel } from '@fluenceur/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BillingTaxProfileFields, type BillingTaxProfileValues } from './BillingTaxProfileFields';
import { BillingVatVerificationStatus } from './BillingVatVerificationStatus';

interface Props {
  context: OrganizationBillingContext;
  onSaved?: () => Promise<unknown> | unknown;
  onDirtyChange?: (dirty: boolean) => void;
  unavailable?: boolean;
}

function profileValues(
  profile: OrganizationBillingTaxProfileData | null,
  context: OrganizationBillingContext,
  suggestion?: CompanyDetails
): BillingTaxProfileValues {
  return {
    companyName: profile?.companyName ?? suggestion?.name ?? '',
    businessType: profile?.businessType ?? suggestion?.businessType ?? '',
    registrationNumber:
      profile?.registrationNumber ?? (!profile ? suggestion?.registrationNumber : '') ?? '',
    country: context.billingCountry ?? profile?.country ?? suggestion?.country ?? '',
    addressLine1: profile?.addressLine1 ?? suggestion?.addressLine1 ?? '',
    addressLine2: profile?.addressLine2 ?? (!profile ? suggestion?.addressLine2 : '') ?? '',
    city: profile?.city ?? suggestion?.city ?? '',
    postalCode: profile?.postalCode ?? suggestion?.postalCode ?? '',
    state: profile?.state ?? '',
    vatNumber: profile?.vatNumber ?? (!profile ? suggestion?.vatNumber : '') ?? '',
  };
}

function ProfileEditor({
  context,
  snapshot,
  suggestion,
  unavailable,
  onSaved,
  onDirtyChange,
}: Props & {
  snapshot: OrganizationBillingTaxProfileResponseData;
  suggestion?: CompanyDetails;
  unavailable: boolean;
}) {
  const { t } = useTranslation();
  const { scope, assertCurrent } = useBillingActionScope();
  const mutation = useUpdateBillingTaxProfile({ context, snapshot });
  const profile = snapshot.profile;
  const fixedCountry = context.billingCountry ?? profile?.country;
  const maximum = (max: number) => t('validation.maxLength', { max });
  const schema = z.object({
    companyName: z
      .string()
      .trim()
      .min(1, t('settings.brandInformationForm.validation.nameRequired'))
      .max(255, maximum(255)),
    businessType: z
      .string()
      .refine((value) => value === 'company' || value === 'individual', t('validation.required')),
    registrationNumber: z.string().trim().max(255, maximum(255)),
    country: z
      .string()
      .refine(isTaxResidenceCountry, t('settings.brandInformationForm.validation.countryRequired')),
    addressLine1: z
      .string()
      .trim()
      .min(1, t('settings.brandInformationForm.validation.addressRequired'))
      .max(255, maximum(255)),
    addressLine2: z.string().trim().max(255, maximum(255)),
    city: z
      .string()
      .trim()
      .min(1, t('settings.brandInformationForm.validation.cityRequired'))
      .max(255, maximum(255)),
    postalCode: z
      .string()
      .trim()
      .min(1, t('settings.brandInformationForm.validation.postalCodeRequired'))
      .max(20, maximum(20)),
    state: z.string().trim().max(255, maximum(255)),
    vatNumber: z.string().trim().max(64, maximum(64)),
  });
  const form = useForm<BillingTaxProfileValues>({
    resolver: zodResolver(schema),
    defaultValues: profileValues(profile, context, suggestion),
  });
  const displayedProfile = useRef(profile?.id ?? null);
  const dirty = form.formState.isDirty;
  const { reset } = form;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (dirty || !profile || displayedProfile.current === profile.id) return;
    displayedProfile.current = profile.id;
    reset(profileValues(profile, context));
  }, [profile, context, dirty, reset]);
  const impersonating = context.cardSetup.exemption === 'impersonation';
  const canManage = snapshot.canManage && context.capabilities.canCommitPaidPlan && !impersonating;
  const busy = mutation.isPending || form.formState.isSubmitting;
  const disabled = !canManage || unavailable || busy;
  const submit = form.handleSubmit(async (value) => {
    if (disabled || (value.businessType !== 'company' && value.businessType !== 'individual'))
      return;
    const target = scope;
    try {
      assertCurrent(target);
      const response = await mutation.mutateAsync({
        expectedProfileId: snapshot.profile?.id ?? null,
        ...value,
        businessType: value.businessType,
        country: fixedCountry ?? value.country,
        registrationNumber: value.registrationNumber || null,
        addressLine2: value.addressLine2 || null,
        state:
          (fixedCountry ?? value.country) === 'US' || (fixedCountry ?? value.country) === 'CA'
            ? value.state || null
            : null,
        vatNumber: value.vatNumber || null,
      });
      assertCurrent(target);
      const saved = readBillingTaxProfile(response);
      if (
        !saved?.profile ||
        saved.organizationId !== target.organizationId ||
        saved.brandId !== target.brandId
      )
        throw new Error('Invalid canonical saved profile');
      reset(profileValues(saved.profile, context));
      await onSaved?.();
    } catch {
      // The mutation translates refusals. Refetching a CAS conflict keeps this draft.
    }
  });
  return (
    <form noValidate onSubmit={submit}>
      <Stack gap={4}>
        {!canManage && (
          <NoticePanel
            tone="neutral"
            title={t(
              impersonating ? 'billing.impersonationReadOnly' : 'billing.taxProfile.readOnly'
            )}
          />
        )}
        <BillingTaxProfileFields form={form} disabled={disabled} fixedCountry={fixedCountry} />
        <BillingVatVerificationStatus
          context={context}
          snapshot={snapshot}
          draftChanged={dirty}
          unavailable={unavailable || busy}
        />
        <Button type="submit" loading={busy} disabled={disabled}>
          {t('common.save')}
        </Button>
      </Stack>
    </form>
  );
}

/** Declared invoice identity, independent of contact identity and tax qualification. */
export function BillingTaxProfileForm({
  context,
  onSaved,
  onDirtyChange,
  unavailable = false,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { scope } = useBillingActionScope();
  const query = useBillingTaxProfileQuery({ context });
  const snapshot = readBillingTaxProfile(query.data);
  const authorized =
    !!scope.userId &&
    context.channel === 'stripe' &&
    !!readBillingContext(context) &&
    context.capabilities.canReadBilling &&
    context.organizationId === scope.organizationId &&
    context.brandId === scope.brandId;
  const scoped =
    authorized &&
    snapshot?.organizationId === scope.organizationId &&
    snapshot.brandId === scope.brandId;
  if (authorized && query.isPending && !query.data) return <Text>{t('common.loading')}</Text>;
  const readError = !scoped || !!query.error;
  return (
    <Stack gap={4}>
      <Heading size="md">{t('billing.taxProfile.title')}</Heading>
      <Text color="fg.muted" fontSize="sm">
        {t('billing.taxProfile.description')}
      </Text>
      {readError && (
        <NoticePanel
          tone="warning"
          title={t('billing.taxProfile.readError')}
          actions={
            <Button variant="outline" onClick={() => void query.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}
      {scoped && snapshot && (
        <ProfileEditor
          key={`${scope.userId}:${scope.organizationId}:${scope.brandId}`}
          context={context}
          snapshot={snapshot}
          suggestion={user?.currentBrand?.companyDetails}
          unavailable={unavailable || !!query.error || query.isFetching}
          onSaved={onSaved}
          onDirtyChange={onDirtyChange}
        />
      )}
    </Stack>
  );
}
